import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useRouteLoaderData } from '@remix-run/react';
import { AudioPlayerElement } from '@shared/dream-components';

import type { CommunityPodcastEpisodeEmbedAttachable } from '~/interfaces/community';
import type { loader as communityLoader } from '~/routes/community';

import { PillLink } from '../ui/PillLink';

import { buildUpgradeUrl } from '../../_utils/buildUpgradeUrl';

interface PodcastEpisodeEmbedCardProps {
  attachable: CommunityPodcastEpisodeEmbedAttachable;
}

export const PodcastEpisodeEmbedCard: FC<PodcastEpisodeEmbedCardProps> = ({ attachable }) => {
  const { t } = useTranslation();
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const isLocked = Boolean(attachable.is_premium && !attachable.audio_url);

  if (isLocked) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-black/10">
        <div aria-hidden="true" className="select-none pointer-events-none blur-[6px]">
          <AudioPlayerElement
            audioUrl=""
            audioType={attachable.audio_type ?? undefined}
            thumbnailUrl={attachable.thumbnail_url}
            title={attachable.title}
            duration={attachable.duration ?? undefined}
            isPlaybackLocked
            options={{
              showThumbnail: true,
              showTitle: true,
              playerMode: 'classic',
              playButtonSize: 28,
              progressBarHeight: 4,
            }}
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <PillLink
            to={buildUpgradeUrl(layoutData?.community?.first_premium_offer_id)}
            variant="primary"
            className="flex items-center gap-1.5"
          >
            <LockClosedIcon aria-hidden="true" className="size-4" />
            {t('community.paid_gate.unlock')}
          </PillLink>
        </div>
      </div>
    );
  }

  return (
    <AudioPlayerElement
      audioUrl={attachable.audio_url ?? ''}
      audioType={attachable.audio_type ?? undefined}
      thumbnailUrl={attachable.thumbnail_url}
      title={attachable.title}
      duration={attachable.duration ?? undefined}
      options={{
        showThumbnail: true,
        showTitle: true,
        playerMode: 'classic',
        playButtonSize: 28,
        progressBarHeight: 4,
      }}
    />
  );
};
